import React, { useEffect, useState } from 'react';
import { RocketLaunchIcon } from '@heroicons/react/24/outline';
import PlaybookHero from '../../../components/playbooks/PlaybookHero';
import PlaybookMetrics from '../../../components/playbooks/PlaybookMetrics';
import VideoEmbed from '../../../components/playbooks/VideoEmbed';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import { getResourceBySlug } from '../../../lib/db';

const FeedbackCultureKeyInfo = () => {
  const [resource, setResource] = useState(null);

  useEffect(() => {
    getResourceBySlug('feedback-culture').then(setResource);
  }, []);

  return (
    <div className="space-y-8">
      <PlaybookHero
        title={resource?.title || "Creating A Feedback Culture That Sticks"}
        subtitle={resource?.description || "A step-by-step guide on establishing a culture of feedback within your startup"}
        category={resource?.category || "Culture & Feedback"}
        bannerUrl={resource?.banner_url}
        readingTime={resource?.timeframe || "15 min read"}
        type={resource?.type}
      />

      <div className="border-l-4 border-primary-light pl-4 italic text-gray-300">
        <p className="mb-4">This Playbook is for you if any of the following (or similar) are true:</p>
        <ul className="space-y-2">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            You've added layers of management and feedback has started moving from being direct to indirect, via others.
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            You are hearing on the grapevine that feedback isn't being given or received by those who needed it or delivered by those who needed to deliver it.
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            You've just joined and are surprised that there isn't a focus on giving and receiving feedback between team members.
          </li>
        </ul>
      </div>

      <PlaybookMetrics
        metrics={[
          { label: 'Difficulty', value: 'Medium - Requires facilitation skills and team coordination' },
          { label: 'Timeline', value: '5 Hours Prep | 1 Hour Call | 1-2 Hours per month Ongoing' },
          { label: 'Team Size', value: 'Ideal for teams of 5-100 people' },
          { label: 'Why Use', value: 'If personal growth is important to your team' },
          { label: 'When to Use', value: 'When you want to build a culture of feedback and growth mindset' }
        ]}
      />

      <PlaybookSection
        title="Watch This"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading
      >
        <VideoEmbed
          src="https://www.loom.com/embed/0879cf983ca845c9b940aa1109d1afd0?sid=1495e48f-73d8-453f-b4d4-6039242b5828"
          title="Feedback Culture Implementation Guide"
        />
      </PlaybookSection>

      <PlaybookSection
        title="What Is This"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading
      >
        <div className="prose prose-invert max-w-none">
          <p className="text-gray-300">
            Welcome to your step-by-step guide on establishing a culture of feedback within your startup. 
            This playbook is designed to help startup people leaders initiate and embed feedback and growth mindset.
          </p>
          <p className="text-gray-300 mt-4">
            Let's get into it 🤘
          </p>
        </div>
      </PlaybookSection>

      <PlaybookSection
        title="What You'll Need"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading
      >
        <ul className="space-y-4">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            Time! A few hours to prepare some foundational material, prep a miro board and run at least 2 x sync sessions
          </li>
        </ul>
      </PlaybookSection>

      <ExpandableCallout
        type="warning"
        title="Common Obstacles"
        defaultOpen={true}
      >
        <div className="space-y-4">
          <p className="text-gray-300">
            When implementing a feedback culture, you might encounter these challenges:
          </p>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              Resistance to change from team members used to indirect feedback
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              Initial discomfort with giving and receiving direct feedback
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              Time constraints in busy startup environments
            </li>
          </ul>
        </div>
      </ExpandableCallout>
    </div>
  );
};

export default FeedbackCultureKeyInfo; 